/**
 * ThinkinCode Landing Page
 * Premium Apple-inspired animations with Lenis + GSAP
 * 
 * Features:
 * - Buttery smooth scrolling with Lenis
 * - Scroll-triggered animations with GSAP ScrollTrigger
 * - Parallax effects and fade-ins
 * - Performance optimized for 60fps
 */

import './styles.css';
import './tailwind.css';
import './apple-carousel.css';
import './works-heymessage.css';
import './animated-card.css';
import './inspira-components.css';
import './glow-border.css';
import './container-scroll.css';
import './text-scroll-reveal.css';
import './mobile-fixes.css';
import './liquid-glass.css';
import { initContainerScroll } from './container-scroll.js';
import { initTextScrollReveal } from './text-scroll-reveal.js';
import { TypingAnimation } from './typing-animation.js';
import { initWorks } from './works.js';
import { initWorksAnimations } from './works-section-animations.js';
import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize Splash Screen
 * Smooth logo fade-in and loading animation
 */
function initSplashScreen() {
  const splashScreen = document.getElementById('splash-screen');
  const splashLogo = document.getElementById('splash-logo');
  const splashLoader = document.querySelector('.splash-loader');
  const splashLoaderLine = document.querySelector('.splash-loader-line');

  if (!splashScreen || !splashLogo || !splashLoader || !splashLoaderLine) {
    // If splash elements don't exist, remove body scroll lock and return
    document.body.style.overflow = '';
    return Promise.resolve();
  }

  // Prevent body scroll during splash
  document.body.style.overflow = 'hidden';

  return new Promise((resolve) => {
    // Create timeline for splash animation
    const tl = gsap.timeline({
      onComplete: () => {
        // Fade out splash screen
        gsap.to(splashScreen, {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.in',
          onComplete: () => {
            splashScreen.classList.add('hidden');
            // Re-enable body scroll
            document.body.style.overflow = '';
            resolve();
          },
        });
      },
    });

    const fast = window.matchMedia?.('(max-width: 768px)')?.matches || false;
    const logoDur = fast ? 0.55 : 0.8;
    const loaderDur = fast ? 0.35 : 0.45;
    const lineDur = fast ? 0.75 : 1.0;

    // Logo animation: fade in and scale up
    tl.to(splashLogo.parentElement, {
      opacity: 1,
      scale: 1,
      duration: logoDur,
      ease: 'power3.out',
    });

    // Show loader
    tl.to(splashLoader, {
      opacity: 1,
      scaleX: 1,
      duration: loaderDur,
      ease: 'power2.out',
    }, '-=0.3');

    // Animate loader line
    tl.to(splashLoaderLine, {
      scaleX: 1,
      duration: lineDur,
      ease: 'power1.inOut',
    }, '-=0.2');

    // No extra hold — improve LCP
    tl.to({}, { duration: 0.0 });
  });
}

/**
 * Initialize Lenis Smooth Scrolling
 */
function initLenis() {
  const lenis = new Lenis({
    duration: 1.0,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 0.8,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
  });

  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);
  return lenis;
}

/**
 * Smooth-scroll in-page anchor links using Lenis
 */
function initSmoothAnchorLinks(lenis) {
  if (!lenis) return;

  const getNavOffset = () => {
    const nav = document.getElementById('main-nav');
    if (!nav) return 0;
    const h = nav.getBoundingClientRect().height || 0;
    // small breathing room so headings aren't glued to the nav
    return Math.ceil(h + 12);
  };

  document.addEventListener('click', (e) => {
    const a = e.target?.closest?.('a[href]');
    if (!a) return;

    const href = a.getAttribute('href') || '';
    if (!href) return;

    // Only handle same-page hash links like "#contact"
    if (!href.startsWith('#')) return;

    const id = href.slice(1);
    if (!id) return;

    const target = document.getElementById(id);
    if (!target) return;

    e.preventDefault();
    lenis.scrollTo(target, { offset: -getNavOffset() });
    history.pushState(null, '', `#${id}`);
  });
}

/**
 * Hero Section Animations
 */
function initHeroAnimations() {
  const hero = document.querySelector('.hero');
  const heroTitle = document.querySelector('.hero-title');
  const heroDescription = document.querySelector('.hero-description');
  const heroCtaGroup = document.querySelector('.hero-cta-group');
  const scrollIndicator = document.querySelector('.scroll-indicator');

  if (!hero) return;

  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    if (heroTitle) gsap.set(heroTitle, { opacity: 1, y: 0 });
    if (heroDescription) gsap.set(heroDescription, { opacity: 1, y: 0 });
    if (heroCtaGroup) gsap.set(heroCtaGroup, { opacity: 1, y: 0 });
  } else {
    if (heroTitle) gsap.set(heroTitle, { opacity: 0, y: 30 });
    if (heroDescription) gsap.set(heroDescription, { opacity: 0, y: 30 });
    if (heroCtaGroup) gsap.set(heroCtaGroup, { opacity: 0, y: 30 });
  }

  if (!isMobile) {
    ScrollTrigger.create({
      trigger: hero,
      start: 'top center',
      onEnter: () => {
        if (heroTitle) {
          gsap.to(heroTitle, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
          });
        }
        if (heroDescription) {
          gsap.to(heroDescription, {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: 0.2,
            ease: 'power3.out',
          });
        }
        if (heroCtaGroup) {
          gsap.to(heroCtaGroup, {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: 0.4,
            ease: 'power3.out',
          });
        }
      },
      onLeaveBack: () => {
        if (heroTitle) gsap.set(heroTitle, { opacity: 1, y: 0 });
        if (heroDescription) gsap.set(heroDescription, { opacity: 1, y: 0 });
        if (heroCtaGroup) gsap.set(heroCtaGroup, { opacity: 1, y: 0 });
      },
    });
  }

  if (scrollIndicator) {
    gsap.set(scrollIndicator, { opacity: 0 });
    ScrollTrigger.create({
      trigger: hero,
      start: 'top center',
      onEnter: () => {
        gsap.to(scrollIndicator, {
          opacity: 1,
          duration: 1,
          ease: 'power2.out',
        });
      },
      onLeave: () => {
        gsap.to(scrollIndicator, {
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
        });
      },
      onEnterBack: () => {
        gsap.to(scrollIndicator, {
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        });
      },
    });
  }
}

/**
 * Tagline Section Animations
 */
function initTaglineAnimations() {
  const tagline = document.querySelector('.tagline');
  const taglineText = document.querySelector('.tagline-text');
  const words = document.querySelectorAll('.tagline-text .word');

  if (!tagline) return;

  const isMobile = window.innerWidth <= 768;
  
  words.forEach((word) => {
    if (isMobile) {
      gsap.set(word, { opacity: 1, y: 0 });
    } else {
      gsap.set(word, { opacity: 1, y: 0 });
      gsap.fromTo(word, {
        opacity: 0.5,
        y: 10,
      }, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.tagline',
          start: 'top 80%',
          toggleActions: 'play none play none',
        },
      });
    }
  });
}

/**
 * Code to App Animations
 * Uses TypingAnimation component for realistic typing effect
 */
function initCodeToAppAnimations() {
  const codeToApp = document.querySelector('.code-to-app');
  const codeStage = document.querySelector('.code-stage');
  const codeLines = document.querySelectorAll('.code-line');
  const transformationVisual = document.querySelector('.transformation-visual');

  if (!codeToApp || !codeStage || !codeLines.length) return;

  // Store original HTML content and create typing animations for each line
  const typingInstances = [];
  const codeTexts = [];
  
  codeLines.forEach((line) => {
    const codeText = line.querySelector('.code-text');
    if (codeText) {
      // Store original HTML with syntax highlighting
      const originalHTML = codeText.innerHTML;
      const plainText = codeText.textContent || '';
      
      // Clear the code-text element and prepare for typing animation
      codeText.innerHTML = '';
      codeText.style.display = 'inline-block';
      
      // Create a container to hold the typed content
      const typingContainer = document.createElement('span');
      typingContainer.className = 'typing-container';
      codeText.appendChild(typingContainer);
      
      // Create TypingAnimation instance
      const typing = new TypingAnimation(typingContainer, {
        speed: 30, // Fast typing speed
        cursor: true,
        cursorChar: '|',
      });
      
      // Set the text to type (we'll use plain text, then restore HTML after)
      typing.text = plainText;
      
      typingInstances.push(typing);
      codeTexts.push({
        element: codeText,
        container: typingContainer,
        typing: typing,
        originalHTML: originalHTML,
        plainText: plainText,
        textLength: plainText.length,
      });
    }
    // Set line opacity and position
    gsap.set(line, { opacity: 1, x: 0 });
  });

  // Set initial states
  if (transformationVisual) gsap.set(transformationVisual, { opacity: 0, scale: 0.8, y: 50 });

  // Calculate total characters for progress calculation
  const totalChars = codeTexts.reduce((sum, item) => sum + item.textLength, 0);

  // Hide the old code cursor element (we're using typing cursor now)
  const oldCursor = document.querySelector('.code-cursor');
  if (oldCursor) {
    oldCursor.style.display = 'none';
  }

  // Create typing animation with scroll trigger
  // Animation must complete before section is passed
  ScrollTrigger.create({
    trigger: codeToApp,
    start: 'top center',
    end: 'bottom center', // Complete animation before section ends
    scrub: 2, // Smooth transition
    onUpdate: (self) => {
      const progress = self.progress;
      // Ensure animation completes at progress 1.0
      const targetCharIndex = Math.floor(progress * totalChars);
      
      // Track which line is currently being typed (for cursor visibility)
      let activeLineIndex = -1;
      
      // Update each code line's typing progress
      let charCount = 0;
      codeTexts.forEach((item, lineIndex) => {
        const lineStart = charCount;
        const lineEnd = charCount + item.textLength;
        
        if (targetCharIndex >= lineEnd) {
          // Line is fully typed - restore original HTML with syntax highlighting
          const currentText = item.container.textContent.replace(/\|/g, '').trim();
          if (currentText !== item.plainText.trim()) {
            item.container.innerHTML = item.originalHTML;
          }
          // Ensure cursor is removed
          const cursor = item.container.querySelector('.typing-cursor');
          if (cursor) cursor.remove();
        } else if (targetCharIndex > lineStart) {
          // Line is partially typed - this is the active line
          activeLineIndex = lineIndex;
          const charsToShow = targetCharIndex - lineStart;
          const lineProgress = Math.min(charsToShow / item.textLength, 1);
          
          // Hide cursor on all other lines
          codeTexts.forEach((otherItem, otherIndex) => {
            if (otherIndex !== lineIndex) {
              const otherCursor = otherItem.container.querySelector('.typing-cursor');
              if (otherCursor) otherCursor.style.display = 'none';
            }
          });
          
          // Show cursor on active line
          item.typing.setProgress(lineProgress);
          const cursor = item.container.querySelector('.typing-cursor');
          if (cursor) cursor.style.display = 'inline-block';
        } else {
          // Line not started yet - reset and hide cursor
          item.typing.setProgress(0);
          const cursor = item.container.querySelector('.typing-cursor');
          if (cursor) cursor.style.display = 'none';
        }
        
        charCount = lineEnd;
      });
      
      // If all lines are complete, ensure all cursors are removed
      if (targetCharIndex >= totalChars) {
        codeTexts.forEach((item) => {
          const cursor = item.container.querySelector('.typing-cursor');
          if (cursor) cursor.remove();
        });
      }
    },
  });

  // Keep code stage visible
  if (codeStage) {
    gsap.set(codeStage, {
      opacity: 1,
      scale: 1,
      y: 0,
    });
  }

  // Animate transformation visual fade in
  if (transformationVisual) {
    gsap.to(transformationVisual, {
      opacity: 1,
      scale: 1,
      y: 0,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: codeToApp,
        start: 'top 20%',
        end: 'top -20%',
        scrub: 1,
      },
    });
  }
}

/**
 * Storytelling Animations
 */
function initStorytellingAnimations() {
  const storyItems = document.querySelectorAll('.story-item');

  if (!storyItems.length) return;

  storyItems.forEach((item) => {
    const visual = item.querySelector('.story-visual');
    const text = item.querySelector('.story-text');
    
    gsap.fromTo(item, {
      opacity: 0.6,
      y: 50,
      scale: 0.95,
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: item,
        start: 'top 85%',
        toggleActions: 'play none play none',
      },
    });

    if (visual) {
      gsap.to(visual, {
        y: -80,
        rotationY: 5,
        scale: 1.05,
        scrollTrigger: {
          trigger: item,
          start: 'top center',
          end: 'bottom top',
          scrub: 1.5,
        },
      });
    }

    if (text) {
      gsap.to(text, {
        y: -40,
        scrollTrigger: {
          trigger: item,
          start: 'top center',
          end: 'bottom top',
          scrub: 1.8,
        },
      });
    }
  });
}

/**
 * Feature Animations
 */
function initFeatureAnimations() {
  const features = document.querySelector('.features');
  const featuresTitle = document.querySelector('.features-title');
  const featureCards = document.querySelectorAll('.feature-card');
  const sectionCta = features?.querySelector('.section-cta');

  if (!features) return;

  if (featuresTitle) {
    gsap.set(featuresTitle, { opacity: 0, y: 30 });
    gsap.to(featuresTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.features',
        start: 'top 80%',
        toggleActions: 'play none play none',
      },
    });
  }

  featureCards.forEach((card, index) => {
    gsap.set(card, { opacity: 0, y: 30 });
    gsap.to(card, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: index * 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        toggleActions: 'play none play none',
      },
    });

    card.addEventListener('mouseenter', () => {
      gsap.to(card, {
        y: -8,
        scale: 1.02,
        duration: 0.4,
        ease: 'power2.out',
      });
    });

    card.addEventListener('mouseleave', () => {
      gsap.to(card, {
        y: 0,
        scale: 1,
        duration: 0.4,
        ease: 'power2.out',
      });
    });
  });

  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * Integrations Animations
 */
function initIntegrationsAnimations() {
  const section = document.getElementById('integrations');
  if (!section) return;

  const title = section.querySelector('.integrations-title');
  const subtitle = section.querySelector('.integrations-subtitle');
  const cards = gsap.utils.toArray('.integration-card');
  const sectionCta = section.querySelector('.section-cta');

  if (title) {
    gsap.set(title, { opacity: 0, y: 30 });
    gsap.to(title, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (subtitle) {
    gsap.set(subtitle, { opacity: 0, y: 30 });
    gsap.to(subtitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 78%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (cards.length) {
    gsap.set(cards, { opacity: 0, y: 40 });

    ScrollTrigger.batch(cards, {
      start: 'top 85%',
      end: 'bottom 10%',
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.08,
        });
      },
      onLeaveBack: (batch) => {
        gsap.to(batch, {
          opacity: 0,
          y: 40,
          duration: 0.6,
          ease: 'power2.out',
        });
      },
    });
  }

  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

  cards.forEach((card) => {
    const toggleCard = () => {
      const wasActive = card.classList.contains('is-flipped');
      cards.forEach((other) => {
        if (other !== card) other.classList.remove('is-flipped');
      });
      if (!wasActive) {
        card.classList.add('is-flipped');
      } else {
        card.classList.remove('is-flipped');
      }
    };

    card.addEventListener('click', (event) => {
      if (hoverMedia.matches) return;
      event.preventDefault();
      toggleCard();
    });

    card.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleCard();
      }
    });

    card.addEventListener(
      'blur',
      () => {
        if (!hoverMedia.matches) {
          card.classList.remove('is-flipped');
        }
      },
      true,
    );
  });

  section.addEventListener('click', (event) => {
    if (hoverMedia.matches) return;
    if (!event.target.closest('.integration-card')) {
      cards.forEach((card) => card.classList.remove('is-flipped'));
    }
  });

  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * Services Animations
 */
function initServicesAnimations() {
  const services = document.querySelector('.services-section');
  const servicesTitle = document.querySelector('.services-title');
  const serviceCards = document.querySelectorAll('.service-card');
  const sectionCta = services?.querySelector('.section-cta');

  if (!services || !serviceCards.length) return;

  if (servicesTitle) {
    gsap.set(servicesTitle, { opacity: 0, y: 30 });
    gsap.to(servicesTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: services,
        start: 'top 80%',
        toggleActions: 'play reverse play reverse',
      },
    });
  }

  serviceCards.forEach((card, index) => {
    gsap.set(card, { opacity: 0, y: 30 });
    gsap.to(card, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: index * 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        toggleActions: 'play reverse play reverse',
      },
    });
  });

  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * Contact Animations
 */
function initContactForm() {
  const form = document.getElementById('contact-form');
  const btn = document.getElementById('form-submit-btn');
  const btnLabel = btn?.querySelector?.('.form-submit-label') || null;
  const successMsg = document.getElementById('form-success-msg');
  if (!form || !btn) return;

  form.addEventListener('submit', async function (e) {
    e.preventDefault();
    const originalText = btnLabel ? btnLabel.textContent : btn.textContent;
    if (btnLabel) btnLabel.textContent = 'Sending…';
    else btn.textContent = 'Sending…';
    btn.classList.remove('is-success');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    try {
      const formData = new FormData(form);
      // Build a plain JSON body for the Cloud Function
      const payload = {
        name: formData.get('name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        message: formData.get('message'),
      };
      const res = await fetch(
        'https://us-central1-thinkincodeza.cloudfunctions.net/sendContactEmail',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        form.querySelectorAll('input:not([type=hidden]), textarea').forEach(el => { el.value = ''; });
        if (successMsg) { successMsg.style.display = 'block'; }
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        btn.setAttribute('aria-busy', 'false');
        if (btnLabel) btnLabel.textContent = 'Sent!';
        else btn.textContent = 'Sent!';
        setTimeout(() => {
          btn.classList.remove('is-success');
          if (btnLabel) btnLabel.textContent = originalText;
          else btn.textContent = originalText;
          btn.disabled = false;
          if (successMsg) { successMsg.style.display = 'none'; }
        }, 5000);
      } else {
        let details = '';
        try {
          const j = await res.json();
          details = j?.error || j?.details || '';
        } catch {}
        throw new Error(details || 'server error');
      }
    } catch (e) {
      btn.classList.remove('is-loading');
      btn.setAttribute('aria-busy', 'false');
      if (btnLabel) btnLabel.textContent = originalText;
      else btn.textContent = originalText;
      btn.disabled = false;
      alert(
        `Something went wrong sending your message. Please email us directly at enquiries@thinkincode.co.za` +
          (e?.message ? `\n\nDetails: ${e.message}` : ''),
      );
    }
  });
}

function initContactAnimations() {
  const contact = document.querySelector('.contact-section');
  const contactTitle = document.querySelector('.contact-title');
  const contactForm = document.querySelector('.contact-form');
  const contactInfo = document.querySelector('.contact-info');
  const contactDetails = document.querySelector('.contact-details');

  if (!contact) return;

  if (contactTitle) {
    gsap.set(contactTitle, { opacity: 0, y: 30 });
    gsap.to(contactTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: contact,
        start: 'top 80%',
        toggleActions: 'play none play none',
      },
    });
  }

  if (contactDetails) {
    gsap.set(contactDetails, { opacity: 0, y: 30 });
    gsap.to(contactDetails, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: contact,
        start: 'top 80%',
        toggleActions: 'play none play none',
      },
    });
  }

  if (contactForm) {
    gsap.set(contactForm, { opacity: 0, x: 30 });
    gsap.to(contactForm, {
      opacity: 1,
      x: 0,
      duration: 1,
      delay: 0.4,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: contact,
        start: 'top 80%',
        toggleActions: 'play none play none',
      },
    });
  }
}

/**
 * Footer Animations
 */
function initFooterAnimations() {
  const footer = document.querySelector('.footer');
  if (!footer) return;

  const footerText = footer.querySelector('p');
  if (footerText) {
    gsap.set(footerText, { opacity: 0, y: 30 });
    gsap.to(footerText, {
      opacity: 1,
      y: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.footer',
        start: 'top 80%',
        toggleActions: 'play none play none',
      },
    });
  }
}

/**
 * Container Text Flip Animation
 */
function initContainerTextFlip() {
  const flipContainers = document.querySelectorAll('.container-text-flip');
  
  flipContainers.forEach(container => {
    // Check if already initialized
    if (container.dataset.initialized === 'true') return;
    
    const wordsData = container.getAttribute('data-words');
    if (!wordsData) return;
    
    // Clear any existing content
    container.innerHTML = '';
    container.style.display = 'inline-block';
    container.style.overflow = 'hidden';
    container.style.verticalAlign = 'bottom';
    
    const words = JSON.parse(wordsData);
    let currentIndex = 0;
    
    const wordElements = words.map((word, index) => {
      const wordEl = document.createElement('span');
      wordEl.className = `flip-word ${index === 0 ? 'active' : ''}`;
      wordEl.textContent = word;
      wordEl.style.position = index === 0 ? 'relative' : 'absolute';
      wordEl.style.left = '0';
      wordEl.style.top = '0';
      wordEl.style.whiteSpace = 'nowrap';
      if (index !== 0) {
        wordEl.style.opacity = '0';
        wordEl.style.visibility = 'hidden';
      }
      container.appendChild(wordEl);
      return wordEl;
    });
    
    // Set initial width immediately
    const firstWord = wordElements[0];
    if (firstWord) {
      // Force a reflow to get accurate width
      firstWord.offsetWidth;
      container.style.width = `${firstWord.offsetWidth}px`;
      container.style.minWidth = `${firstWord.offsetWidth}px`;
    }
    
    // Update width after fonts load (if needed)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (firstWord) {
          const width = firstWord.offsetWidth;
          container.style.width = `${width}px`;
          container.style.minWidth = `${width}px`;
        }
      });
    }
    
    function animateToNext() {
      const currentWord = wordElements[currentIndex];
      const nextIndex = (currentIndex + 1) % words.length;
      const nextWord = wordElements[nextIndex];
      
      if (currentWord && nextWord) {
        const nextWidth = nextWord.offsetWidth;
        
        const tl = gsap.timeline({
          onComplete: () => {
            currentWord.style.position = 'absolute';
            nextWord.style.position = 'relative';
            currentIndex = nextIndex;
          }
        });
        
        tl.to(container, {
          width: `${nextWidth}px`,
          duration: 0.7,
          ease: 'power2.inOut',
        }, 0);
        
        tl.to(currentWord, {
          opacity: 0,
          y: -100,
          duration: 0.7,
          ease: 'power2.inOut',
          onComplete: () => {
            currentWord.style.visibility = 'hidden';
          }
        }, 0);
        
        tl.fromTo(nextWord, {
          opacity: 0,
          y: 100,
          visibility: 'visible',
        }, {
          opacity: 1,
          y: 0,
          visibility: 'visible',
          duration: 0.7,
          ease: 'power2.inOut',
        }, 0);
      }
    }
    
    container.dataset.initialized = 'true';
    setInterval(animateToNext, 3000);
  });
}

/**
 * Flip Words Animation
 */
function initFlipWords() {
  const flipContainers = document.querySelectorAll('.flip-words');
  
  flipContainers.forEach(container => {
    // Check if already initialized
    if (container.dataset.initialized === 'true') return;
    
    const wordsData = container.getAttribute('data-words');
    if (!wordsData) return;
    
    // Clear any existing content
    container.innerHTML = '';
    container.style.display = 'inline-block';
    container.style.position = 'relative';
    container.style.overflow = 'visible';
    
    const words = JSON.parse(wordsData);
    let currentIndex = 0;
    
    const wordElements = words.map((word, index) => {
      const wordEl = document.createElement('span');
      wordEl.className = `flip-word ${index === 0 ? 'active' : ''}`;
      wordEl.textContent = word;
      wordEl.style.position = index === 0 ? 'relative' : 'absolute';
      wordEl.style.left = '0';
      wordEl.style.top = '0';
      wordEl.style.whiteSpace = 'nowrap';
      if (index !== 0) {
        wordEl.style.opacity = '0';
        wordEl.style.visibility = 'hidden';
      }
      container.appendChild(wordEl);
      return wordEl;
    });
    
    // Set initial width
    const firstWord = wordElements[0];
    if (firstWord) {
      firstWord.offsetWidth; // Force reflow
      container.style.width = `${firstWord.offsetWidth}px`;
      container.style.minWidth = `${firstWord.offsetWidth}px`;
    }
    
    function flipToNext() {
      const currentWord = wordElements[currentIndex];
      const nextIndex = (currentIndex + 1) % words.length;
      const nextWord = wordElements[nextIndex];
      
      if (currentWord && nextWord) {
        // Calculate widths
        const currentWidth = currentWord.offsetWidth;
        const nextWidth = nextWord.offsetWidth;
        const maxWidth = Math.max(currentWidth, nextWidth);
        
        const tl = gsap.timeline({
          onComplete: () => {
            currentWord.style.position = 'absolute';
            currentWord.style.visibility = 'hidden';
            nextWord.style.position = 'relative';
            nextWord.style.visibility = 'visible';
            currentIndex = nextIndex;
            container.style.width = `${nextWidth}px`;
          }
        });
        
        // Animate container width if needed
        if (currentWidth !== nextWidth) {
          tl.to(container, {
            width: `${maxWidth}px`,
            duration: 0.6,
            ease: 'power2.inOut',
          }, 0);
        }
        
        tl.to(currentWord, {
          opacity: 0,
          rotationX: -90,
          duration: 0.6,
          ease: 'power2.in',
        }, 0);
        
        tl.fromTo(nextWord, {
          opacity: 0,
          rotationX: 90,
          visibility: 'visible',
        }, {
          opacity: 1,
          rotationX: 0,
          visibility: 'visible',
          duration: 0.6,
          ease: 'power2.out',
        }, 0.1);
      }
    }
    
    container.dataset.initialized = 'true';
    setInterval(flipToNext, 2500);
  });
}

/**
 * Animated List
 */
function initAnimatedList() {
  const lists = document.querySelectorAll('.animated-list');
  
  lists.forEach(list => {
    const items = Array.from(list.querySelectorAll('.notification-item'));
    if (items.length === 0) return;
    
    const notifications = items.map((item, index) => ({
      name: item.getAttribute('data-name') || '',
      description: item.getAttribute('data-description') || '',
      icon: item.getAttribute('data-icon') || '',
      color: item.getAttribute('data-color') || 'rgba(0, 201, 167, 0.2)',
      element: item,
    }));
    
    items.forEach(item => {
      item.style.display = 'none';
      gsap.set(item, { opacity: 0, scale: 0 });
    });
    
    const displayedItems = [];
    let nextIndex = 0;

    // Premium Lucide-style icons (consistent cross-device)
    // Keep the existing data-icon keys, but map them to better icons.
    const ICONS = {
      // Unmatched Quality
      sparkles: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6l4 1 3-4 3 4 4-1-2 4 2 4-4-1-3 4-3-4-4 1 2-4z"/><path d="M6 20h12"/></svg>`,
      // Exceptional Performance
      bolt: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12a8 8 0 0 1 16 0"/><path d="M12 12v6"/><path d="M8 18h8"/><path d="M12 12l3-3"/><path d="M12 12l-3-3"/></svg>`,
      // Cutting-Edge Technology
      rocket: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="6" height="6" rx="1"/><path d="M9 2v3"/><path d="M15 2v3"/><path d="M9 19v3"/><path d="M15 19v3"/><path d="M2 9h3"/><path d="M2 15h3"/><path d="M19 9h3"/><path d="M19 15h3"/></svg>`,
      // Premium Design
      'pen-tool': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3a7 7 0 0 0 0 14c2.7 0 4-1.3 4-4H12"/><path d="M16.5 7.5a1 1 0 1 0 .001 0Z"/><path d="M12 6a1 1 0 1 0 .001 0Z"/><path d="M7.5 7.5a1 1 0 1 0 .001 0Z"/></svg>`,
    };
    
    function renderNotification(notification, element) {
      const iconKey = String(notification.icon || '').trim();
      const iconSvg = ICONS[iconKey] || ICONS.sparkles;
      element.innerHTML = `
        <div class="notification-inner">
          <div class="notification-icon-wrapper" style="background-color: ${notification.color}">
            <span class="notification-icon" aria-hidden="true">${iconSvg}</span>
          </div>
          <div class="notification-content-wrapper">
            <div class="notification-header">
              <span class="notification-name">${notification.name}</span>
            </div>
            <p class="notification-description">${notification.description}</p>
          </div>
        </div>
      `;
      element.style.display = 'block';
      gsap.set(element, { opacity: 0, scale: 0 });
    }
    
    function addNotification(notification) {
      const element = notification.element;
      renderNotification(notification, element);
      list.insertBefore(element, list.firstChild);
      displayedItems.unshift({ notification, element });
      
      gsap.fromTo(element, {
        opacity: 0,
        scale: 0.8,
        y: 30,
      }, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.7,
        ease: 'back.out(1.2)',
      });
    }
    
    async function startLoop() {
      while (displayedItems.length < notifications.length) {
        const notification = notifications[nextIndex];
        addNotification(notification);
        nextIndex = (nextIndex + 1) % notifications.length;
        if (displayedItems.length < notifications.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    ScrollTrigger.create({
      trigger: list.parentElement || list,
      start: 'top 80%',
      onEnter: () => {
        if (displayedItems.length === 0) {
          startLoop();
        }
      },
    });
  });
}

/**
 * Inspira Timeline
 */
function initInspiraTimeline() {
  const timeline = document.querySelector('.inspira-timeline');
  const timelineItems = document.querySelectorAll('.timeline-item');
  const timelineLine = document.querySelector('.timeline-line');
  const footer = document.querySelector('.footer');
  
  if (!timeline || !timelineItems.length) return;

  // Timeline stays at bottom, can move under footer
  // Timeline is now positioned after footer in HTML, so it will naturally be at the bottom
  gsap.set(timeline, { bottom: 0 });
  
  // Allow timeline to move under footer when footer is in view
  if (footer) {
    ScrollTrigger.create({
      trigger: footer,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.5,
      onUpdate: (self) => {
        const progress = self.progress;
        const footerHeight = footer.offsetHeight;
        // Timeline can move under footer - negative bottom value moves it up
        // At progress 0: timeline at bottom (0)
        // At progress 1: timeline moves up by footer height (under footer)
        const newBottom = footerHeight * progress;
        gsap.set(timeline, { bottom: -newBottom });
      },
      onEnter: () => {
        // Footer is in view, timeline can move under it
        const footerHeight = footer.offsetHeight;
        gsap.set(timeline, { bottom: -footerHeight });
      },
      onLeaveBack: () => {
        // Scrolling back up, timeline returns to bottom
        gsap.set(timeline, { bottom: 0 });
      },
    });
  }
  
  const sectionMap = [
    { key: 'home', selector: '#home, .hero' },
    { key: 'tagline', selector: '.tagline' },
    { key: 'code-to-app', selector: '.code-to-app' },
    { key: 'storytelling', selector: '.storytelling' },
    { key: 'services', selector: '#services, .services-section' },
    { key: 'features', selector: '.features' },
    { key: 'faq', selector: '.faq-section' },
    { key: 'contact', selector: '#contact, .contact-section' },
  ];
  
  const sections = sectionMap.map(({ selector }) => {
    return document.querySelector(selector);
  }).filter(Boolean);
  
  if (sections.length === 0) return;
  
  ScrollTrigger.create({
    trigger: document.body,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 0.5,
    onUpdate: (self) => {
      if (!timelineLine || sections.length === 0) return;
      
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const firstSection = sections[0];
      const lastSection = sections[sections.length - 1];
      if (!firstSection || !lastSection) return;
      
      const firstSectionTop = firstSection.offsetTop;
      const lastSectionBottom = lastSection.offsetTop + lastSection.offsetHeight;
      const totalHeight = lastSectionBottom - firstSectionTop;
      const scrollProgress = scrollTop - firstSectionTop;
      let progress = Math.max(0, Math.min(1, scrollProgress / totalHeight));
      
      timelineLine.style.setProperty('--timeline-progress', `${progress * 100}%`);
    },
    invalidateOnRefresh: true,
  });
  
  sectionMap.forEach(({ key, selector }, index) => {
    const section = document.querySelector(selector);
    if (!section) return;
    
    const timelineItem = Array.from(timelineItems).find(item => 
      item.getAttribute('data-section') === key
    );
    
    if (timelineItem) {
      ScrollTrigger.create({
        trigger: section,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => {
          timelineItems.forEach(item => {
            item.classList.remove('active', 'past');
          });
          timelineItem.classList.add('active');
          for (let i = 0; i < index; i++) {
            const prevItem = Array.from(timelineItems).find(item => 
              item.getAttribute('data-section') === sectionMap[i].key
            );
            if (prevItem) prevItem.classList.add('past');
          }
        },
        onEnterBack: () => {
          timelineItems.forEach(item => {
            item.classList.remove('active', 'past');
          });
          timelineItem.classList.add('active');
          for (let i = 0; i < index; i++) {
            const prevItem = Array.from(timelineItems).find(item => 
              item.getAttribute('data-section') === sectionMap[i].key
            );
            if (prevItem) prevItem.classList.add('past');
          }
        },
      });
    }
  });
  
  if (timelineItems.length > 0) {
    timelineItems[0].classList.add('active');
  }
}

/**
 * Testimonials Marquee
 */
function initTestimonialsAnimations() {
  const testimonials = document.querySelector('.testimonials');
  const testimonialsTitle = document.querySelector('.testimonials-title');
  const marqueeFirst = document.querySelector('.marquee-first .marquee-content');
  const marqueeSecond = document.querySelector('.marquee-second .marquee-content');

  if (!testimonials || !marqueeFirst || !marqueeSecond) return;

  // Testimonials data – real client reviews
  const reviews = [
    {
      name: "Taj Printers",
      username: "Marketing Team",
      body: "We are extremely happy with the website created for us by ThinkinCode. The final outcome exceeded our expectations, and the entire process was handled professionally. Their team provides excellent ongoing support, and whenever any issues arise, they respond promptly and resolve them efficiently. It has been a pleasure working with them on the Taj Printers website.",
    },
    {
      name: "PadelEngine User",
      username: "PadelEngine App by ThinkinCode",
      body: "Running a competitive padel tournament requires precision, real-time updates, and smooth player coordination. This software delivered on every level. Registration, scheduling, draws, and live scoring were handled effortlessly, creating a professional and exciting experience for players and spectators alike. The system's ability to manage brackets and communicate updates instantly made tournament day stress-free. Players loved the transparency, and our organising team appreciated how intuitive and reliable the platform was. We will absolutely be using this software for all future tournaments.",
    },
    {
      name: "East Rand Tennis League",
      username: "Admin - ThinkinCode Meets Tennis App",
      body: "Managing an ongoing tennis league involves tracking fixtures, standings, player availability, and results across multiple weeks. This software made the entire process efficient and transparent. The automated scheduling and ranking updates kept players engaged and informed throughout the season. Communication tools allowed us to send updates instantly, reducing confusion and last-minute issues. The platform brought professionalism and structure to our league, elevating the overall experience for participants. We highly recommend it to any sporting organisation looking to modernise and streamline their operations.",
    },
    {
      name: "Sabiha",
      username: "Ministry of Fun",
      body: "As a party planning company, our website needed to be vibrant, stylish, and easy for clients to navigate, and this developer delivered exactly that. The design perfectly reflects our brand personality while still being professional and polished. Thinkincode created beautiful galleries to showcase our events, streamlined enquiry forms for quick bookings, and ensured the site loads quickly across all devices. Clients constantly compliment us on how visually appealing and easy to use our website is. The entire process was smooth, collaborative, and efficient. Their creativity, technical expertise, and ability to understand our vision truly set them apart. We couldn't be happier with the final result.",
    },
    {
      name: "Ayesha's Real Estate",
      username: "Real Estate",
      body: "We recently worked with this web developer to build our real estate website's competition landing page, and the results have exceeded our expectations. The site is modern, fast, and incredibly user-friendly, with a clean layout that showcases our property listings beautifully. They understood the importance of lead generation in real estate and strategically incorporated enquiry forms, property filters, and clear calls-to-action throughout the site. The mobile responsiveness is excellent, which has significantly improved client engagement. What truly stood out was their attention to detail and understanding of the property industry. The website not only looks professional but functions seamlessly, helping us convert more visitors into serious buyers and sellers. Highly recommended for any real estate business looking to elevate their online presence.",
    },
    {
      name: "Muhammad",
      username: "Bright Minds Learn",
      body: "ThinkInCode built my website 'brightmindslearn.co.za' exactly how I wanted it. The whole process was smooth, communication was great, and my site looks clean and professional. Really happy with the service.",
    },
    {
      name: "S&K Showroom / StreetGym",
      username: "KlicnGo App by ThinkinCode",
      body: "We've been using KlicnGo to manage both our online and in-store collection orders, and it's made a big difference to our workflow. Our online team loads the orders onto the portal, and the stores can clearly see what needs to be prepared for collection. The Pay Now, Collect Later feature works really well for us. Customers can pay upfront, we source the item from another store if needed, and once it arrives, the system sends out email and SMS notifications. The same notification process also works smoothly for our regular online collection orders. The OTP system at collection is also a great feature - it adds security and keeps the handover process controlled and professional. Overall, it's helped us manage multi-store orders much more efficiently.",
    },
    {
      name: "OffCourt",
      username: "OffCourt App by ThinkinCode",
      body: "Really happy with the work done on the OffCourt App. The live scoring feature works smoothly and has added a whole new level to our league. The player stats system are clean, easy to use, and run properly. Communication was good throughout and everything was delivered the way we needed it. Solid work overall, definitely recommend if you're looking for someone reliable who knows what they're doing. Brilliant ideas were brought forward by ThinkinCode and we really appreciate the time and effort put into building this amazing app.",
    },
    {
      name: "SSS Cellular",
      username: "Various tools built by ThinkinCode",
      body: "We recently worked with ThinkinCode on the development of several custom applications, including an SMS system, a logistics webhook tool, Damage Report generator, QC Checklist, and Dispatch Manifest tool. The team demonstrated strong technical expertise and delivered solutions that were practical, user-friendly, and tailored to our operational needs. Each tool has streamlined our processes, improved efficiency, and enhanced communication across departments. Overall, ThinkinCode provided reliable service, innovative design, and effective implementation. We appreciate their professionalism and commitment to quality, and we look forward to future collaborations.",
    },
    {
      name: "Mashooq Abdulla",
      username: "Founder & Business Consultant, MPower Business OPS Efficiencies",
      body: "ThinkinCode built my website. Dynamic, efficient, effective, supportive, forward-thinking, collaborative, guiding. Some of their many strong points. I'll gladly recommend them to my entire client portfolio.",
    },
  ];

  // Split reviews into two rows
  const firstRow = reviews.slice(0, Math.ceil(reviews.length / 2));
  const secondRow = reviews.slice(Math.ceil(reviews.length / 2));

  // Avatar colors
  const avatarColors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  ];

  const TRUNCATE_LENGTH = 160;

  function truncateAtWord(str, maxLen) {
    if (str.length <= maxLen) return str;
    const cut = str.slice(0, maxLen);
    const lastSpace = cut.lastIndexOf(' ');
    return lastSpace > maxLen * 0.5 ? cut.slice(0, lastSpace) : cut;
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Function to create a card (preview + full text via data, expand on "Read more")
  function createCard(review, index) {
    const initials = review.name.split(' ').map(n => n[0]).join('');
    const color = avatarColors[index % avatarColors.length];
    const isLong = review.body.length > TRUNCATE_LENGTH;
    const preview = isLong ? truncateAtWord(review.body, TRUNCATE_LENGTH) : review.body;
    const fullText = review.body;
    const dataFull = escapeAttr(fullText);

    return `
      <div class="marquee-card" data-fulltext="${dataFull}">
        <div class="marquee-card-content">
          <div class="marquee-card-text">
            <span class="marquee-card-text-preview">"${escapeAttr(preview)}${isLong ? '...' : ''}"</span>
            <span class="marquee-card-text-full" aria-hidden="true"></span>
          </div>
          ${isLong ? '<button type="button" class="marquee-card-read-more" aria-expanded="false">Read more</button>' : ''}
          <div class="marquee-card-author">
            <div class="marquee-card-avatar" style="background: ${color}">${initials}</div>
            <div class="marquee-card-info">
              <div class="marquee-card-name">${review.name}</div>
              <div class="marquee-card-username">${review.username}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function bindTestimonialExpand() {
    document.querySelectorAll('.marquee-card[data-fulltext]').forEach((card) => {
      const fullSpan = card.querySelector('.marquee-card-text-full');
      const previewSpan = card.querySelector('.marquee-card-text-preview');
      const btn = card.querySelector('.marquee-card-read-more');
      if (!fullSpan || !btn) return;
      const raw = card.getAttribute('data-fulltext');
      if (raw) fullSpan.textContent = '"' + raw + '"';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = card.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded);
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  }

  // Duplicate cards for seamless loop
  function populateMarquee(container, row) {
    const cards = row.map((review, index) => createCard(review, index)).join('');
    container.innerHTML = cards + cards;
  }

  populateMarquee(marqueeFirst, firstRow);
  populateMarquee(marqueeSecond, secondRow);
  bindTestimonialExpand();

  // Animate title
  if (testimonialsTitle) {
    gsap.set(testimonialsTitle, { opacity: 0, y: 30 });
    gsap.to(testimonialsTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.testimonials',
        start: 'top 80%',
        toggleActions: 'play reverse play reverse',
      },
    });
  }

  const sectionCta = testimonials.querySelector('.section-cta');
  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * FAQ Accordion
 */
function initFAQ() {
  const faqItems = document.querySelectorAll('.faq-item');
  
  if (faqItems.length === 0) {
    console.warn('No FAQ items found');
    return;
  }
  
  faqItems.forEach((item) => {
    const question = item.querySelector('.faq-question');
    const answer = item.querySelector('.faq-answer');
    const answerContent = item.querySelector('.faq-answer-content');
    
    if (!question || !answer || !answerContent) {
      console.warn('FAQ item missing required elements', item);
      return;
    }
    
    // Set initial state - ensure answer is hidden
    answer.style.maxHeight = '0';
    answer.style.overflow = 'hidden';
    answerContent.style.opacity = '0';
    answerContent.style.visibility = 'hidden';
    answerContent.style.transform = 'translateY(-10px)';
    
    question.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const isActive = item.classList.contains('active');
      
      // Close all other items
      faqItems.forEach((otherItem) => {
        if (otherItem !== item && otherItem.classList.contains('active')) {
          const otherAnswer = otherItem.querySelector('.faq-answer');
          const otherContent = otherItem.querySelector('.faq-answer-content');
          const otherQuestion = otherItem.querySelector('.faq-question');
          
          if (otherAnswer && otherContent && otherQuestion) {
            otherItem.classList.remove('active');
            otherQuestion.setAttribute('aria-expanded', 'false');
            
            otherAnswer.style.maxHeight = '0';
            gsap.to(otherContent, {
              opacity: 0,
              y: -10,
              visibility: 'hidden',
              duration: 0.3,
              ease: 'power2.out',
            });
          }
        }
      });
      
      // Toggle current item
      if (isActive) {
        // Close
        item.classList.remove('active');
        question.setAttribute('aria-expanded', 'false');
        
        answer.style.maxHeight = '0';
        gsap.to(answerContent, {
          opacity: 0,
          y: -10,
          visibility: 'hidden',
          duration: 0.3,
          ease: 'power2.out',
        });
      } else {
        // Open
        item.classList.add('active');
        question.setAttribute('aria-expanded', 'true');
        
        // Get the actual height
        answer.style.maxHeight = 'none';
        const height = answer.scrollHeight;
        answer.style.maxHeight = '0';
        
        // Force reflow
        answer.offsetHeight;
        
        // Animate to full height using CSS transition
        requestAnimationFrame(() => {
          answer.style.maxHeight = height + 'px';
        });
        
        // Animate content fade-in
        requestAnimationFrame(() => {
          gsap.to(answerContent, {
            opacity: 1,
            y: 0,
            visibility: 'visible',
            duration: 0.3,
            delay: 0.1,
            ease: 'power2.out',
          });
        });
      }
    });
  });
}

/**
 * Box Reveal Animation
 * https://inspira-ui.com/docs/components/text-animations/box-reveal
 */
function initBoxReveal() {
  const boxReveals = document.querySelectorAll('.box-reveal');
  
  boxReveals.forEach((boxReveal) => {
    // Get custom properties
    const color = boxReveal.dataset.color || '#000000';
    
    // Set CSS custom properties
    boxReveal.style.setProperty('--box-reveal-color', color);
    
    // Get content element (first child - the h2 or p)
    const content = boxReveal.firstElementChild;
    
    if (!content) return;
    
    // Create a wrapper div for the reveal box (since we can't animate ::before directly)
    const revealBox = document.createElement('div');
    revealBox.className = 'box-reveal-overlay';
    revealBox.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${color};
      z-index: 10;
      pointer-events: none;
      transform: translateX(-100%);
    `;
    // Insert overlay before content (content will now be second child)
    boxReveal.insertBefore(revealBox, content);
    
    // Content is now the second child (after overlay)
    const actualContent = boxReveal.children[1];
    
    // Set initial states
    gsap.set(revealBox, {
      x: '-100%',
      force3D: true,
    });
    
    gsap.set(actualContent, {
      opacity: 0,
      force3D: true,
    });
    
    // Create smooth scrubbed animation
    const revealTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: boxReveal,
        start: 'top 85%',
        end: 'top 50%',
        scrub: 0.5,
      },
    });
    
    // Animate reveal box sliding
    revealTimeline.to(revealBox, {
      x: '100%',
      ease: 'none',
      force3D: true,
    });
    
    // Animate content fade in (starts after 30% of reveal)
    const contentTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: boxReveal,
        start: 'top 85%',
        end: 'top 50%',
        scrub: 0.5,
      },
    });
    
    contentTimeline.to(actualContent, {
      opacity: 1,
      ease: 'none',
      force3D: true,
    }, 0.3); // Start at 30% progress
  });
}

/**
 * Glow Borders
 */
function initGlowBorders() {
  const glowCards = document.querySelectorAll('.bento-card.glow-border, .feature-card.glow-border');
  
  glowCards.forEach((card) => {
    ScrollTrigger.create({
      trigger: card,
      start: 'top 80%',
      onEnter: () => {
        card.classList.add('active');
      },
      onLeaveBack: () => {
        card.classList.remove('active');
      },
    });
  });
}

/**
 * Stats Animations
 */
function initStatsAnimations() {
  const stats = document.querySelector('.stats');
  const statNumbers = document.querySelectorAll('.stat-number');

  if (!stats || !statNumbers.length) return;

  statNumbers.forEach((statNumber) => {
    const target = parseFloat(statNumber.getAttribute('data-count') || '0');
    const suffix = statNumber.getAttribute('data-suffix') || '';
    const multiplier = parseInt(statNumber.getAttribute('data-multiplier') || '1', 10);
    const isDecimal = !Number.isInteger(target);

    const formatDisplay = (v) => {
      const n = Math.floor(v);
      const value = multiplier > 1 ? n * multiplier : n;
      const str = multiplier > 1 ? value.toLocaleString() : (isDecimal ? v.toFixed(1) : String(n));
      return str + suffix;
    };

    gsap.set(statNumber, { textContent: formatDisplay(0) });

    ScrollTrigger.create({
      trigger: statNumber,
      start: 'top 85%',
      onEnter: () => {
        gsap.to({ value: 0 }, {
          value: target,
          duration: 2,
          ease: 'power2.out',
          onUpdate: function() {
            statNumber.textContent = formatDisplay(this.targets()[0].value);
          },
        });
      },
      once: true,
    });
  });
}

/**
 * Process Animations
 */
function initProcessAnimations() {
  const process = document.querySelector('.process');
  const processTitle = document.querySelector('.process-title');
  const processSteps = document.querySelectorAll('.process-step');
  const sectionCta = process?.querySelector('.section-cta');

  if (!process) return;

  if (processTitle) {
    gsap.set(processTitle, { opacity: 0, y: 30 });
    gsap.to(processTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: process,
        start: 'top 80%',
        toggleActions: 'play reverse play reverse',
      },
    });
  }

  processSteps.forEach((step, index) => {
    gsap.set(step, { opacity: 0, y: 30 });
    gsap.to(step, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay: index * 0.15,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: step,
        start: 'top 85%',
        toggleActions: 'play reverse play reverse',
      },
    });
  });

  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * Technologies Animations
 */
function initTechnologiesAnimations() {
  const technologies = document.querySelector('.technologies');
  const technologiesTitle = document.querySelector('.technologies-title');
  const platformCards = document.querySelectorAll('.platform-card');
  const sectionCta = technologies?.querySelector('.section-cta');

  if (!technologies) return;

  if (technologiesTitle) {
    gsap.set(technologiesTitle, { opacity: 0, y: 30 });
    gsap.to(technologiesTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: technologies,
        start: 'top 80%',
        toggleActions: 'play reverse play reverse',
      },
    });
  }

  if (platformCards.length) {
    gsap.set(platformCards, { opacity: 0, y: 40, rotateX: -12 });

    ScrollTrigger.batch(platformCards, {
      start: 'top 85%',
      end: 'bottom 10%',
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.12,
        });
      },
      onLeaveBack: (batch) => {
        gsap.to(batch, {
          opacity: 0,
          y: 40,
          rotateX: -10,
          duration: 0.5,
          ease: 'power2.out',
        });
      },
    });
  }

  if (sectionCta) {
    gsap.set(sectionCta, { opacity: 0, y: 30 });
    gsap.to(sectionCta, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: sectionCta,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }
}

/**
 * Platform Card Tilt Interaction
 */
function initPlatformCards() {
  const cards = document.querySelectorAll('.platform-card');
  if (!cards.length) return;

  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

  cards.forEach((card) => {
    const inner = card.querySelector('.platform-card-inner');
    if (!inner) return;

    const updatePointerPosition = (event) => {
      const bounds = card.getBoundingClientRect();
      const offsetX = event.clientX - bounds.left;
      const offsetY = event.clientY - bounds.top;
      const centerX = bounds.width / 2;
      const centerY = bounds.height / 2;

      const rotateY = ((offsetX - centerX) / centerX) * 12;
      const rotateX = ((centerY - offsetY) / centerY) * 12;

      inner.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      card.style.setProperty('--pointer-x', `${(offsetX / bounds.width) * 100}%`);
      card.style.setProperty('--pointer-y', `${(offsetY / bounds.height) * 100}%`);
    };

    const resetCard = () => {
      inner.style.transform = 'rotateX(0deg) rotateY(0deg)';
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
    };

    const enableListeners = () => {
      card.addEventListener('mousemove', updatePointerPosition);
      card.addEventListener('mouseleave', resetCard);
    };

    const disableListeners = () => {
      card.removeEventListener('mousemove', updatePointerPosition);
      card.removeEventListener('mouseleave', resetCard);
      resetCard();
    };

    const handleMediaChange = (matches) => {
      if (matches) {
        enableListeners();
      } else {
        disableListeners();
      }
    };

    handleMediaChange(hoverMedia.matches);

    if (typeof hoverMedia.addEventListener === 'function') {
      hoverMedia.addEventListener('change', (event) => handleMediaChange(event.matches));
    } else if (typeof hoverMedia.addListener === 'function') {
      hoverMedia.addListener((event) => handleMediaChange(event.matches));
    }

    card.addEventListener('focus', () => {
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
      inner.style.transform = 'rotateX(0deg) rotateY(0deg)';
    });

    card.addEventListener('blur', () => {
      resetCard();
    });
  });
}

/**
 * Initialize Light Speed Background
 */
function initLightSpeed() {
  const container = document.getElementById('light-speed-container');
  if (!container) return;

  // Respect reduced motion and data-saver preferences
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const saveData = navigator?.connection?.saveData;
  if (reduceMotion || saveData) {
    container.style.background = '#121212';
    return;
  }

  const isMobile = window.innerWidth <= 768;

  const sharedColors = {
    background: 0xffffff,
    roadColor: 0x080808,
    islandColor: 0x0a0a0a,
    shoulderLines: 0x131318,
    brokenLines: 0x131318,
    leftCars: [0xDC143C, 0xFF2400, 0xFF1744],
    rightCars: [0x65B3A9, 0x65B3A9, 0x65B3A9],
    sticks: 0xD6FB43,
  };

  const mobileOverrides = {
    length: 260,
    roadWidth: 9,
    islandWidth: 1.6,
    lanesPerRoad: 3,
    fov: 72,
    fovSpeedUp: 110,
    movingAwaySpeed: [40, 55],
    movingCloserSpeed: [-90, -120],
    lightPairsPerRoadWay: 20,
    totalSideLightSticks: 14,
    carLightsLength: [260 * 0.04, 260 * 0.14],
    carLightsRadius: [0.04, 0.1],
    speedUp: 1.4,
  };

  const options = {
    distortion: 'turbulentDistortion',
    colors: sharedColors,
    ...(isMobile ? mobileOverrides : {}),
  };

  // Lazy-load the heavy WebGL module to reduce initial JS payload
  import('./light-speed-app.js')
    .then(({ LightSpeedApp }) => {
      try {
        const lightSpeedApp = new LightSpeedApp(container, options);
        lightSpeedApp.loadAssets().then(() => {
          lightSpeedApp.init();
        });
        window.lightSpeedInstance = lightSpeedApp;
      } catch (error) {
        console.warn('Light Speed effect failed to initialize:', error);
        container.style.background = '#121212';
      }
    })
    .catch((error) => {
      console.warn('Light Speed module failed to load:', error);
      container.style.background = '#121212';
    });
}

// Liquid Glass nav effect removed (keeping standard backdrop blur styling).

/**
 * Navigation: scroll animation + mobile menu
 */
function initNavInteractions() {
  const nav = document.getElementById('main-nav');
  if (!nav) return;

  // If the nav is React-mounted (inside #site-nav), don't attach vanilla listeners.
  // React controls open/close state and renders the curved mobile menu overlay.
  if (nav.closest && nav.closest('#site-nav')) return;

  const toggle = nav.querySelector('.nav-toggle');
  const menu = nav.querySelector('.nav-menu');

  // Mobile menu toggle
  const setOpen = (open) => {
    nav.classList.toggle('is-mobile-open', open);
    if (toggle) {
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    }
    // Prevent background scroll when menu is open on mobile
    if (window.innerWidth <= 768) {
      document.documentElement.style.overflow = open ? 'hidden' : '';
      document.body.style.overflow = open ? 'hidden' : '';
    }
  };

  if (toggle && menu) {
    toggle.addEventListener('click', () => setOpen(!nav.classList.contains('is-mobile-open')));

    // Close on link click
    menu.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', () => setOpen(false));
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') setOpen(false);
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!nav.classList.contains('is-mobile-open')) return;
      if (nav.contains(e.target)) return;
      setOpen(false);
    });

    // If resizing to desktop, reset mobile state
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) setOpen(false);
    });
  }

  // Scroll behavior: subtle shrink + hide on scroll down, show on scroll up
  let lastY = window.scrollY;
  let ticking = false;
  const onScroll = () => {
    const y = window.scrollY;
    const isScrolled = y > 12;
    nav.classList.toggle('is-scrolled', isScrolled);

    lastY = y;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(onScroll);
  }, { passive: true });
  onScroll();
}

/**
 * Initialize Fluid Cursor
 */
function initFluidCursor() {
  // Only initialize on desktop (WebGL can be heavy on mobile)
  const isMobile = window.innerWidth < 768;
  if (isMobile) {
    return;
  }

  // Create container for fluid cursor
  const container = document.body;
  
  // Lazy-load the WebGL cursor module to reduce initial JS payload
  import('./fluid-cursor.js')
    .then(({ FluidCursor }) => {
      try {
        const fluidCursor = new FluidCursor({
          simResolution: 128,
          dyeResolution: 1440,
          shading: true,
          transparent: true,
        });

        fluidCursor.init(container);
        window.fluidCursorInstance = fluidCursor;
      } catch (error) {
        console.warn('Fluid Cursor failed to initialize:', error);
      }
    })
    .catch((error) => {
      console.warn('Fluid Cursor module failed to load:', error);
    });
}

/**
 * Clients Section Animations (for main page)
 */
function initClientsAnimations() {
  const clientsSection = document.querySelector('.clients-section');
  if (!clientsSection) return;

  const clientsTitle = clientsSection.querySelector('.clients-title');
  const clientsIntro = clientsSection.querySelector('.clients-intro');
  const clientsGrid = clientsSection.querySelector('.clients-grid');

  if (clientsTitle) {
    gsap.set(clientsTitle, { opacity: 0, y: 30 });
    gsap.to(clientsTitle, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: clientsTitle,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (clientsIntro) {
    gsap.set(clientsIntro, { opacity: 0, y: 30 });
    gsap.to(clientsIntro, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: clientsIntro,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  }

  if (clientsGrid) {
    const clientItems = clientsGrid.querySelectorAll('.client-item');
    if (clientItems.length) {
      gsap.set(clientItems, { opacity: 0, y: 30 });
      ScrollTrigger.batch(clientItems, {
        start: 'top 85%',
        onEnter: (batch) => {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.05,
          });
        },
      });
    }
  }
}

/**
 * Floating Contact Widget
 */
function initFloatingContact() {
  const widget = document.getElementById('floating-contact');
  const toggle = widget?.querySelector('.floating-contact-toggle');
  if (!widget || !toggle) return;

  toggle.addEventListener('click', () => {
    const isOpen = widget.hasAttribute('data-open');
    if (isOpen) {
      widget.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      widget.setAttribute('data-open', '');
      toggle.setAttribute('aria-expanded', 'true');
    }
  });

  // Close when clicking outside
  document.addEventListener('click', (e) => {
    if (widget.hasAttribute('data-open') && !widget.contains(e.target)) {
      widget.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });
}

/**
 * Floating Back to Top Button
 */
function initBackToTop() {
  const btn = document.getElementById('floating-backtotop');
  if (!btn) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Show fairly early so it's easy to find on shorter pages too
  const showAfter = 220;

  const setVisible = (visible) => {
    btn.classList.toggle('is-visible', visible);
  };

  const updateVisibility = () => {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    setVisible(y >= showAfter);
  };

  btn.addEventListener('click', () => {
    // Close floating contact menu if open, so the UI doesn't overlap
    const widget = document.getElementById('floating-contact');
    const toggle = widget?.querySelector('.floating-contact-toggle');
    if (widget?.hasAttribute('data-open')) {
      widget.removeAttribute('data-open');
      toggle?.setAttribute('aria-expanded', 'false');
    }

    const lenis = window.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(0, { immediate: prefersReduced });
      return;
    }
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  });

  window.addEventListener('scroll', updateVisibility, { passive: true });
  window.addEventListener('resize', updateVisibility, { passive: true });
  updateVisibility();
}

/**
 * Initialize all animations
 */
function init() {
  const lenis = initLenis();
  // expose for other modules / debugging
  window.lenis = lenis;

  // Lenis smooth-scroll for in-page anchor clicks
  initSmoothAnchorLinks(lenis);

  // Set footer year dynamically
  const footerYear = document.getElementById('footer-year');
  if (footerYear) footerYear.textContent = new Date().getFullYear();

  // Initialize Light Speed background first
  initLightSpeed();

  // Animated / responsive navigation behavior
  initNavInteractions();
  
  // Initialize Fluid Cursor
  initFluidCursor();

  // Initialize works section (must be before animations)
  // Only initialize if works section exists
  if (document.getElementById('works-projects-container')) {
    initWorks();
  }

  // Mount the home-page FocusRail lazily (React chunk) to reduce initial JS.
  const mountFocusRail = () =>
    import('./focus-rail-mount.tsx')
      .then((m) => m.mountHomeFocusRail?.())
      .catch(() => {});
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => mountFocusRail(), { timeout: 2500 });
  } else {
    setTimeout(() => mountFocusRail(), 1200);
  }
  
  // Initialize all animations
  initHeroAnimations();
  initTaglineAnimations();
  initCodeToAppAnimations();
  initStorytellingAnimations();
  initFeatureAnimations();
  initIntegrationsAnimations();
  initServicesAnimations();
  initWorksAnimations();
  initContactAnimations();
  initFooterAnimations();
  initContainerTextFlip();
  initFlipWords();
  initAnimatedList();
  initGlowBorders();
  
  // Initialize container scroll after other animations
  // This needs to happen after Lenis is fully initialized
  initContainerScroll();
  initTestimonialsAnimations();
  initStatsAnimations();
  initProcessAnimations();
  initTechnologiesAnimations();
  initPlatformCards();
  initFAQ();
  initBoxReveal();
  initTextScrollReveal();
  initFloatingContact();
  initBackToTop();
  initContactForm();

  // Refresh ScrollTrigger after all animations are initialized
  // This ensures all ScrollTriggers are properly calculated, especially with Lenis
  requestAnimationFrame(() => {
    ScrollTrigger.refresh();
    // Double refresh to ensure Lenis positions are accounted for
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 200);
  });

  window.addEventListener('resize', () => {
    ScrollTrigger.refresh();
  });

  // Mobile: refresh when viewport settles (address bar hide/show) and on orientation change
  if (window.innerWidth <= 768) {
    window.addEventListener('orientationchange', () => {
      setTimeout(() => ScrollTrigger.refresh(), 300);
    });
    // Delayed refresh for mobile viewport settling
    setTimeout(() => ScrollTrigger.refresh(), 500);
    setTimeout(() => ScrollTrigger.refresh(), 1000);
  }

  return () => {
    lenis.destroy();
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', async () => {
    await initSplashScreen();
    init();
  });
} else {
  initSplashScreen().then(() => {
    init();
  });
}

export { init };
